import * as React from 'react'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useTranslations } from 'next-intl'
import { useFeatureIsOn, useFeatureValue } from 'growthbook'
import { IconViewQuilt, IconRocketLaunch } from '@repo/design-system-cf/components/icons'

import type { IconProps } from '../../assets/icons'
import {
  HomeIcon,
  ProjectsIcon,
  DigitalAssetsIcon,
  DiscoverDesignersIcon,
  LearnIcon,
  TutorialIcon,
  YourClubIcon,
  DiscussionsIcon,
  InviteMembersIcon,
  FileSharingIcon,
  GroupsIcon,
  EventsIcon,
  TrustedVendorsIcon,
  KnowledgeHubIcon,
  TrendsIcon,
  StoreIcon,
  MembersIcon,
  QuizzesIcon,
  LightBulbIcon,
  InformationIcon,
  NewsIcon,
} from '../../assets/icons'
import {
  FEATURE_CLUBHOUSE_NEWS,
  FEATURE_CLUBHOUSE_STORE,
  FEATURE_CLUBHOUSE_TRENDS,
  FEATURE_CLUBHOUSES_DISCOVER_DESIGNERS,
  FEATURE_CLUBHOUSES_EVENTS,
  FEATURE_CLUBHOUSES_FILE_SHARING,
  FEATURE_CLUBHOUSES_GROUPS,
  FEATURE_CLUBHOUSES_TRUSTED_VENDORS,
  FEATURE_CLUBHOUSES_YOUR_CLUB,
  FEATURE_CLUBHOUSES_CLASSES,
  FEATURE_CLUBHOUSES_TUTORIALS,
  FEATURE_CLUBHOUSE_ENABLED_KNOWLEDGE_HUBS,
  FEATURE_CLUBHOUSE_GETTING_STARTED,
} from '../../_utils/features'
import type { KnowledgeHub, NavigationBox } from '@repo/payload-kit/payload-types'

export interface ItemModel {
  id?: string
  path?: string
  icon?: React.FC<IconProps>
  text: string
  type: 'link' | 'paragraph'
  isActive: boolean
}

export interface MobileMenuItemModel extends ItemModel {
  items?: MobileMenuItemModel[]
}

// oxlint-disable-next-line complexity
export function useMenuModels({
  clubhouseSlug,
  knowledgeHubRouteConfig,
}: {
  clubhouseSlug: string
  knowledgeHubRouteConfig?: KnowledgeHub | null
}) {
  const t = useTranslations('pages.clubhouse')
  const isClassesOn = useFeatureIsOn(FEATURE_CLUBHOUSES_CLASSES)
  const isTutorialsOn = useFeatureIsOn(FEATURE_CLUBHOUSES_TUTORIALS)
  const isNewsOn = useFeatureIsOn(FEATURE_CLUBHOUSE_NEWS)
  const isTrendsOn = useFeatureIsOn(FEATURE_CLUBHOUSE_TRENDS)

  const enabledKnowledgeHubs = useFeatureValue<string[]>(
    FEATURE_CLUBHOUSE_ENABLED_KNOWLEDGE_HUBS,
    [],
  )
  const enabledGettingStarted = useFeatureValue<string[]>(FEATURE_CLUBHOUSE_GETTING_STARTED, [])
  const isCurrentKnowledgeHubOn = enabledKnowledgeHubs.includes(clubhouseSlug)
  const isGettingStartedOn = enabledGettingStarted.includes(clubhouseSlug)

  // Growthbook was problemtatic with refreshing the Localstorage, so we use a hardcoded list for now
  const enabledQuizzes = ['cricut', 'siser', 'xtool', 'rhinestones', 'paper-crafts', 'christmas']
  const isQuizzesOn = enabledQuizzes.includes(clubhouseSlug)

  const isInformationOn = isNewsOn || isCurrentKnowledgeHubOn || isTrendsOn

  const sidebarModel: ItemModel[] = [
    {
      text: t('sidebar.title'),
      type: 'paragraph',
      isActive: true,
      icon: HomeIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/`,
      icon: HomeIcon,
      text: t('sidebar.home'),
      type: 'link',
      isActive: true,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/members/`,
      icon: MembersIcon,
      text: t('sidebar.members_stats'),
      type: 'link',
      isActive: true,
    },
    {
      text: t('sidebar.inspiration'),
      type: 'paragraph',
      isActive: true,
      icon: LightBulbIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/inspiration/projects/`,
      icon: ProjectsIcon,
      text: t('sidebar.projects'),
      type: 'link',
      isActive: true,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/inspiration/assets/`,
      icon: DigitalAssetsIcon,
      text: t('sidebar.digital_assets'),
      type: 'link',
      isActive: true,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/inspiration/designers/`,
      icon: DiscoverDesignersIcon,
      text: t('sidebar.discover_designers'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_DISCOVER_DESIGNERS),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/templates/`,
      icon: IconViewQuilt,
      text: t('sidebar.templates'),
      type: 'link',
      isActive: true,
    },
    {
      text: t('sidebar.learn'),
      type: 'paragraph',
      isActive: isClassesOn || isTutorialsOn || isGettingStartedOn,
      icon: LearnIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/learn/classes/`,
      icon: LearnIcon,
      text: t('sidebar.classes'),
      type: 'link',
      isActive: isClassesOn,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/learn/tutorials/`,
      icon: TutorialIcon,
      text: t('sidebar.tutorials'),
      type: 'link',
      isActive: isTutorialsOn,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/learn/getting-started/`,
      icon: IconRocketLaunch,
      text: t('sidebar.getting_started'),
      type: 'link',
      isActive: isGettingStartedOn,
    },
    {
      text: t('sidebar.social'),
      type: 'paragraph',
      isActive: true,
      icon: DiscussionsIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/your-club/`,
      icon: YourClubIcon,
      text: t('sidebar.your_club'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_YOUR_CLUB),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/discussions/`,
      icon: DiscussionsIcon,
      text: t('sidebar.discussions'),
      type: 'link',
      isActive: true,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/members/invite/`,
      icon: InviteMembersIcon,
      text: t('sidebar.invite_members'),
      type: 'link',
      isActive: true,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/files/`,
      icon: FileSharingIcon,
      text: t('sidebar.file_sharing'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_FILE_SHARING),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/groups`,
      icon: GroupsIcon,
      text: t('sidebar.groups'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_GROUPS),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/events/`,
      icon: EventsIcon,
      text: t('sidebar.events'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_EVENTS),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/vendors/`,
      icon: TrustedVendorsIcon,
      text: t('sidebar.trusted_vendors'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSES_TRUSTED_VENDORS),
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/quizzes/`,
      icon: QuizzesIcon,
      text: t('sidebar.quizzes'),
      type: 'link',
      isActive: isQuizzesOn,
    },
    {
      text: t('sidebar.information'),
      type: 'paragraph',
      isActive: isInformationOn,
      icon: InformationIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/news/`,
      icon: NewsIcon,
      text: t('sidebar.news'),
      type: 'link',
      isActive: isNewsOn,
    },
    {
      id: 'knowledge-hub',
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/knowledge-hub/`,
      icon: KnowledgeHubIcon,
      text: t('sidebar.knowledge_hub'),
      type: 'link',
      isActive: isCurrentKnowledgeHubOn,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/trends/`,
      icon: TrendsIcon,
      text: t('sidebar.trends'),
      type: 'link',
      isActive: isTrendsOn,
    },
    {
      text: t('sidebar.shop'),
      type: 'paragraph',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSE_STORE),
      icon: StoreIcon,
    },
    {
      path: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/store/`,
      icon: StoreIcon,
      text: t('sidebar.store'),
      type: 'link',
      isActive: useFeatureIsOn(FEATURE_CLUBHOUSE_STORE),
    },
  ]
  const mobileMenuModel = []
  let currentGroup: MobileMenuItemModel | null = null

  for (const item of sidebarModel) {
    if (item.id === 'knowledge-hub' && knowledgeHubRouteConfig) {
      const navigationBox = knowledgeHubRouteConfig.navigationBox as NavigationBox
      ;(item as MobileMenuItemModel).items = []
      for (const khItem of knowledgeHubRouteConfig.routes || []) {
        const mobileItem: MobileMenuItemModel = {
          path: khItem.links?.length
            ? undefined
            : khItem.externalLink ||
              `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/knowledge-hub/${khItem.path}`,
          isActive: true,
          text: khItem.title || '',
          type: 'link',
          items:
            khItem.type === 'navigationBox'
              ? navigationBox?.categories?.map(category => ({
                  text: category.label || '',
                  type: 'paragraph',
                  isActive: true,
                  items: category.columns
                    ?.flatMap(column => column.items)
                    .map(innerItem => ({
                      text: innerItem?.label || '',
                      type: 'link',
                      isActive: true,
                      link: `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/knowledge-hub/${innerItem?.href}`,
                    })),
                }))
              : khItem.links?.map(link => ({
                  path:
                    link.externalLink ||
                    `${AppRoutes.CLUBHOUSE}${clubhouseSlug}/knowledge-hub/${khItem.path}/${
                      link.path
                    }`,
                  isActive: true,
                  text: link.label || '',
                  type: 'link',
                })) || [],
        }
        ;(item as MobileMenuItemModel)?.items?.push(mobileItem)
      }
    }
    if (item.type === 'paragraph') {
      currentGroup = {
        ...item,
        items: [],
      }
      mobileMenuModel.push(currentGroup)
    } else if (currentGroup) {
      currentGroup?.items?.push(item)
    }
  }
  return { sidebarModel, mobileMenuModel }
}
